import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Lightbulb, Plus, Spinner, Warning } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { api } from "@/lib/api";
import { toast } from "sonner";

/**
 * Step 4 of 4: auto-run the AI shopping-list check on the in-memory draft,
 * surface required + additional suggestions, then create the project on confirm.
 * No project row exists until the final CTA is pressed.
 */
export default function FinalMaterialsReviewPage() {
  const [draft, setDraft] = useState(null);
  const [review, setReview] = useState(null);     // AIHelpResponse from /draft-review
  const [reviewError, setReviewError] = useState(null);
  const [picked, setPicked] = useState({});       // index -> bool
  const [phase, setPhase] = useState("loading");  // loading | result | error | saving
  const navigate = useNavigate();

  useEffect(() => {
    const raw = sessionStorage.getItem("halfjob_draft");
    if (!raw) {
      navigate("/projects/new", { replace: true });
      return;
    }
    const d = JSON.parse(raw);
    setDraft(d);
    runReview(d);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const runReview = async (d) => {
    try {
      setPhase("loading");
      setReviewError(null);
      const r = await api.post("/projects/draft-review", {
        title: d.title,
        description: d.description || "",
        location: d.location || "",
        steps: d.steps || [],
        materials: d.materials || [],
        difficulty: d.difficulty,
        estimated_minutes: d.estimated_minutes,
        safety_warnings: d.safety_warnings || [],
      });
      setReview(r.data);
      // Pre-select all "required" suggestions by default.
      const initial = {};
      (r.data?.suggested_new_materials || []).forEach((m, i) => {
        if (m.required) initial[i] = true;
      });
      setPicked(initial);
      setPhase("result");
    } catch (e) {
      const detail = e?.response?.data?.detail;
      const msg = typeof detail === "object" ? detail?.message : detail;
      const friendly = msg || "Couldn't run the materials review";
      toast.error(friendly);
      setReview(null);
      setReviewError({
        message: friendly,
        status: e?.response?.status,
        kind: typeof detail === "object" ? detail?.kind : null,
        retry_after_seconds: typeof detail === "object" ? detail?.retry_after_seconds : null,
      });
      setPhase("error");
    }
  };

  const createProject = async () => {
    if (!draft) return;
    const suggestions = review?.suggested_new_materials || [];
    const extras = suggestions
      .map((m, i) => (picked[i] ? m : null))
      .filter(Boolean)
      .map((m) => ({
        name: m.name,
        quantity: m.quantity || "",
        confidence: null,
        purchase_suggestion: null,
        required: !!m.required,
        explanation: m.explanation || m.notes || "",
        user_status: "need_to_buy",
      }));

    const finalMaterials = [...(draft.materials || []), ...extras];
    try {
      setPhase("saving");
      const r = await api.post("/projects/from-breakdown", {
        title: draft.title,
        description: draft.description || "",
        location: draft.location || "",
        cover_photo_id: draft.cover_photo_id || null,
        photo_ids: draft.photo_ids || [],
        steps: draft.steps || [],
        materials: finalMaterials,
        difficulty: draft.difficulty,
        estimated_minutes: draft.estimated_minutes,
        safety_warnings: draft.safety_warnings || [],
        confidence: draft.confidence,
        beginner_questions: draft.beginner_questions || [],
      });
      sessionStorage.removeItem("halfjob_draft");
      toast.success("Project created");
      navigate(`/projects/${r.data.id}`, { replace: true });
    } catch (e) {
      toast.error("Could not create project");
      setPhase("result");
    }
  };

  if (!draft) return null;

  const all = review?.suggested_new_materials || [];
  const required = all.map((m, i) => ({ m, i })).filter(({ m }) => m.required);
  const additional = all.map((m, i) => ({ m, i })).filter(({ m }) => !m.required);
  const requiredPickedCount = required.filter(({ i }) => picked[i]).length;
  const additionalPickedCount = additional.filter(({ i }) => picked[i]).length;

  return (
    <div className="min-h-screen pb-32" data-testid="final-materials-review-page">
      <header className="px-5 pt-6 pb-3 max-w-md mx-auto w-full">
        <button onClick={() => navigate(-1)} className="flex items-center gap-1 text-timber-700 font-bold" data-testid="final-back">
          <ArrowLeft size={18} weight="bold" /> Back
        </button>
        <p className="label-cap mt-3">Step 4 of 4 · Final materials review</p>
        <h1 className="font-heading text-2xl font-bold mt-1">{draft.title}</h1>
        <p className="text-sm text-timber-700 mt-1">
          The AI re-read your plan and is checking for anything you might be missing.
        </p>
      </header>

      <main className="px-5 max-w-md mx-auto w-full space-y-4">
        {phase === "loading" && (
          <div className="tactile-card p-8 flex flex-col items-center gap-3" data-testid="final-loading">
            <Spinner size={36} className="text-masking animate-spin" />
            <p className="text-sm font-bold text-timber-700">Reviewing tools, materials and consumables…</p>
          </div>
        )}

        {phase === "error" && (
          <div
            data-testid="final-error"
            className="tactile-card p-5 border-2 border-block/40 bg-[#FBEDEE] flex flex-col gap-3"
          >
            <div className="flex items-start gap-2">
              <Warning size={20} weight="fill" className="text-block flex-shrink-0 mt-0.5" />
              <div className="min-w-0 flex-1">
                <p className="font-bold text-timber-900">The materials review didn't run</p>
                <p className="text-sm text-timber-700 mt-0.5">
                  {reviewError?.message || "Couldn't reach the AI just now."}
                </p>
                {reviewError?.retry_after_seconds && (
                  <p className="text-xs text-timber-500 mt-1">
                    Try again in about {Math.max(1, Math.ceil(reviewError.retry_after_seconds / 60))} min.
                  </p>
                )}
              </div>
            </div>
            <div className="flex gap-2">
              <Button
                data-testid="final-retry-btn"
                onClick={() => runReview(draft)}
                className="flex-1 bg-masking text-timber-900 hover:bg-masking-600 hover:text-white min-h-[48px] font-bold"
              >
                Try again
              </Button>
              <Button
                data-testid="final-skip-btn"
                onClick={createProject}
                variant="outline"
                className="flex-1 border-2 border-stone_border min-h-[48px] font-bold"
              >
                Skip & create project
              </Button>
            </div>
          </div>
        )}

        {phase !== "loading" && phase !== "error" && review?.observation && (
          <div className="bg-kraft border-2 border-stone_border rounded-xl p-3 text-sm text-timber-900" data-testid="final-observation">
            <Lightbulb size={14} weight="fill" className="inline text-masking mr-1" />
            {review.observation}
          </div>
        )}

        {required.length > 0 && (
          <section data-testid="final-required-section">
            <h2 className="label-cap text-block flex items-center gap-1">
              <Warning size={12} weight="fill" /> Required items
            </h2>
            <p className="text-xs text-timber-700 mt-0.5">
              These are needed for the project to be doable.
            </p>
            <div className="space-y-1.5 mt-2">
              {required.map(({ m, i }) => (
                <SuggestionRow
                  key={i}
                  index={i}
                  m={m}
                  checked={!!picked[i]}
                  onToggle={(v) => setPicked((p) => ({ ...p, [i]: v }))}
                  variant="required"
                />
              ))}
            </div>
          </section>
        )}

        {additional.length > 0 && (
          <section data-testid="final-additional-section">
            <h2 className="label-cap text-denim flex items-center gap-1">
              <Plus size={12} weight="bold" /> Additional suggestions
            </h2>
            <p className="text-xs text-timber-700 mt-0.5">
              Nice-to-haves that will improve safety or quality.
            </p>
            <div className="space-y-1.5 mt-2">
              {additional.map(({ m, i }) => (
                <SuggestionRow
                  key={i}
                  index={i}
                  m={m}
                  checked={!!picked[i]}
                  onToggle={(v) => setPicked((p) => ({ ...p, [i]: v }))}
                  variant="additional"
                />
              ))}
            </div>
          </section>
        )}

        {phase === "result" && all.length === 0 && (
          <div className="tactile-card p-6 text-center" data-testid="final-empty">
            <p className="text-sm text-timber-700">
              All good — the AI didn't flag any missing items.
            </p>
          </div>
        )}

        {phase !== "error" && (
          <>
            <div className="bg-chalk border-2 border-stone_border rounded-xl p-3 text-[11px] text-timber-700" data-testid="final-summary">
              Adding <strong className="text-timber-900">{requiredPickedCount}</strong> required ·{" "}
              <strong className="text-timber-900">{additionalPickedCount}</strong> additional to your shopping list.
            </div>

            <Button
              data-testid="final-create-btn"
              onClick={createProject}
              disabled={phase === "loading" || phase === "saving"}
              className="w-full bg-masking text-timber-900 hover:bg-masking-600 hover:text-white min-h-[64px] text-lg font-bold btn-press"
            >
              {phase === "saving" ? "Creating project…" : "Create Project & Begin →"}
            </Button>
          </>
        )}
      </main>
    </div>
  );
}

function SuggestionRow({ index, m, checked, onToggle, variant }) {
  return (
    <label
      data-testid={`final-suggestion-${index}`}
      className={`flex items-start gap-2 cursor-pointer rounded-lg p-2.5 border-2 ${
        variant === "required"
          ? "bg-[#FBEDEE] border-block/40 hover:border-block"
          : "bg-chalk border-stone_border hover:border-denim"
      }`}
    >
      <Checkbox
        checked={checked}
        onCheckedChange={(v) => onToggle(!!v)}
        className="mt-0.5"
      />
      <div className="min-w-0 flex-1">
        <p className="text-sm font-semibold text-timber-900">
          {m.name}
          {m.quantity && <span className="text-timber-500 font-normal"> · {m.quantity}</span>}
        </p>
        {(m.explanation || m.notes) && (
          <p className="text-xs text-timber-700 italic mt-0.5">{m.explanation || m.notes}</p>
        )}
      </div>
    </label>
  );
}
